/**
 * EventLogPane — React renderer for EventLogPaneModel
 *
 * Columnar operational replay log optimized for scanability,
 * replay cognition, and cross-pane trace alignment.
 *
 * REFINEMENTS (V1.1):
 * - Columnar layout: LEVEL → EVENT → MESSAGE → ACTOR → SPAN ID → TIME
 * - Compact, aligned, single-line rows for replay scanability
 * - Lightweight sorting support for LEVEL, EVENT, ACTOR, TIME
 * - Sticky headers for column navigation
 * - Hover-highlight only (calm selection motion)
 */

import React, { useState, useCallback, useMemo } from 'react';
import type { EventLogPaneModel, EventLogRow } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { severityToColor, NEUTRAL, TYPOGRAPHY, ROW_HEIGHT, SPACING } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface EventLogPaneProps {
  model: EventLogPaneModel;
  onSelectEvent?: (eventId: string) => void;
}

type SortField = 'level' | 'event' | 'actor' | 'time' | null;
type SortDirection = 'asc' | 'desc';

const COLUMN_WIDTHS = {
  level: 70,
  event: 120,
  message: 'auto',
  actor: 140,
  spanId: 100,
  time: 90,
} as const;

function getLevelColor(level: string): { bg: string; fg: string } {
  const l = level.toUpperCase();
  if (l === 'OK' || l === 'PASS') return { bg: '#0a2010', fg: '#5fa35f' };
  if (l === 'WARN' || l === 'DEGRADED') return { bg: '#201a10', fg: '#f5a35f' };
  if (l === 'ERROR' || l === 'REFUSED') return { bg: '#200a0a', fg: '#f55f5f' };
  if (l === 'INFO') return { bg: '#101020', fg: '#a35ff5' };
  return { bg: NEUTRAL.bg400, fg: NEUTRAL.text400 };
}

function formatCompactTime(timestamp: string): string {
  if (!timestamp) return '—';
  return timestamp.slice(11, 23);
}

function formatCompactSpanId(spanId?: string): string {
  if (!spanId) return '—';
  return spanId.slice(0, 12) + '…';
}

function getRowStyle(row: EventLogRow, isHovered: boolean): React.CSSProperties {
  const color = severityToColor(row.severity);
  return {
    display: 'flex',
    alignItems: 'center',
    height: ROW_HEIGHT.compact.tableRow,
    padding: `0 ${SPACING.sp3}px`,
    fontSize: TYPOGRAPHY.body.size,
    lineHeight: `${TYPOGRAPHY.body.lineHeight}px`,
    color: NEUTRAL.text500,
    borderLeft: `2px solid ${row.isSelected ? color.border : 'transparent'}`,
    backgroundColor: row.isFocused ? NEUTRAL.bg400 : row.isSelected ? NEUTRAL.bg500 : isHovered ? NEUTRAL.bg300 : 'transparent',
    cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif',
    borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
  };
}

export const EventLogPane: React.FC<EventLogPaneProps> = ({ model, onSelectEvent }) => {
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [hoveredSeq, setHoveredSeq] = useState<number | null>(null);

  const sortedRows = useMemo(() => {
    if (!sortField) return model.rows;
    const sorted = [...model.rows];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'level':
          comparison = a.severity.localeCompare(b.severity);
          break;
        case 'event':
          comparison = (a.eventType || '').localeCompare(b.eventType || '');
          break;
        case 'actor':
          comparison = (a.actor || '').localeCompare(b.actor || '');
          break;
        case 'time':
          comparison = a.timestamp.localeCompare(b.timestamp);
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [model.rows, sortField, sortDirection]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Event log error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.heading.size,
        fontWeight: TYPOGRAPHY.heading.weight,
        color: NEUTRAL.text600,
        backgroundColor: NEUTRAL.bg600,
        height: ROW_HEIGHT.compact.spanHeader,
      }}>
        <span>Event Log</span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.totalRows} rows · {model.refusalCount} refusals
        </span>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: `${SPACING.sp1}px ${SPACING.sp3}px`,
        backgroundColor: NEUTRAL.bg600,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 700,
        color: NEUTRAL.text400,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <div style={{ width: COLUMN_WIDTHS.level, flexShrink: 0, cursor: 'pointer' }} onClick={() => handleSort('level')}>
          LEVEL {sortField === 'level' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div style={{ width: COLUMN_WIDTHS.event, flexShrink: 0, cursor: 'pointer' }} onClick={() => handleSort('event')}>
          EVENT {sortField === 'event' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          MESSAGE
        </div>
        <div style={{ width: COLUMN_WIDTHS.actor, flexShrink: 0, cursor: 'pointer' }} onClick={() => handleSort('actor')}>
          ACTOR {sortField === 'actor' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div style={{ width: COLUMN_WIDTHS.spanId, flexShrink: 0 }}>
          SPAN ID
        </div>
        <div style={{ width: COLUMN_WIDTHS.time, flexShrink: 0, cursor: 'pointer' }} onClick={() => handleSort('time')}>
          TIME {sortField === 'time' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sortedRows.map((row) => (
          <div
            key={row.seq}
            style={getRowStyle(row, hoveredSeq === row.seq)}
            onMouseEnter={() => setHoveredSeq(row.seq)}
            onMouseLeave={() => setHoveredSeq(null)}
            onClick={() => onSelectEvent?.(row.eventId)}
          >
            <div style={{ width: COLUMN_WIDTHS.level, flexShrink: 0 }}>
              <span style={{
                fontSize: TYPOGRAPHY.micro.size,
                fontWeight: 700,
                color: getLevelColor(row.severity).fg,
                backgroundColor: getLevelColor(row.severity).bg,
                padding: '1px 4px',
                borderRadius: 3,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {row.severity}
              </span>
            </div>

            <div style={{ width: COLUMN_WIDTHS.event, flexShrink: 0, fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.eventType || '—'}
            </div>

            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: TYPOGRAPHY.label.size, color: row.isRefusal ? '#e03030' : NEUTRAL.text500 }}>
              {row.label}
            </div>

            <div style={{ width: COLUMN_WIDTHS.actor, flexShrink: 0, fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.actor || '—'}
            </div>

            <div style={{ width: COLUMN_WIDTHS.spanId, flexShrink: 0, fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatCompactSpanId(row.spanId)}
            </div>

            <div style={{ width: COLUMN_WIDTHS.time, flexShrink: 0, fontFamily: TYPOGRAPHY.mono.family, fontSize: TYPOGRAPHY.mono.size, color: NEUTRAL.text400 }}>
              {formatCompactTime(row.timestamp)}
            </div>
          </div>
        ))}

        {sortedRows.length === 0 && (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' }}>
            No events
          </div>
        )}
      </div>
    </div>
  );
};
